import { useSeo } from "@/lib/seo";
import { useState } from "react";
import AppHeader from '@/pages/home/components/AppHeader';
import Footer from '@/pages/home/components/Footer';
import AppEnding from '@/pages/home/components/AppEnding';
import Reveal from '@/pages/lot-size/components/Reveal';
import { useHashScroll } from '@/hooks/useHashScroll';
import { flashTarget } from '@/hooks/flashTarget';
import ScenarioCards from './components/ScenarioCards';
import StudioNav from './components/StudioNav';
import SpreadSection from './components/SpreadSection';
import SwapSection from './components/SwapSection';
import ProductSection from './components/ProductSection';
import WorkflowSection from './components/WorkflowSection';
import ToolChooser from './components/ToolChooser';
import MethodologySection from './components/MethodologySection';
import NotesSection from './components/NotesSection';
import BrokerSection from './components/BrokerSection';

export default function ComparePage() {
  useSeo({
    /* @replace:compare-title */
    title: 'Đối chiếu Thị trường Forex | So sánh Giá, Spread & Sàn Giao dịch - ForexMatrix',
    /* @replace:compare-description */
    description: 'Công cụ Đối chiếu Thị trường (Market Comparison) của ForexMatrix giúp bạn so sánh trực quan các điều kiện giao dịch, mức Spread, hoa hồng và tính năng giữa các nhà môi giới hàng đầu một cách nhanh chóng và minh bạch.',
    /* @replace:compare-keywords */
    keywords: 'đối chiếu thị trường forex, so sánh sàn forex, so sánh spread, market comparison forex, đánh giá nhà môi giới, forex matrix',
    path: '/doi-chieu-thi-truong',
  });
  useHashScroll();
  const [active, setActive] = useState('spread');

  const goTo = (anchor: string) => {
    const el = document.getElementById(anchor);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      flashTarget(el);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#F8FAFC] text-slate-900">
      <AppHeader />
      <main className="pb-8">
        <div className="mx-auto max-w-6xl px-4 pt-28 pb-16 md:px-8 md:pt-32 md:pb-20">
          {/* Header */}
          <Reveal>
            <div className="mt-6 max-w-3xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100">
                <i className="ri-arrow-left-right-line" />
                Market Comparison Studio
              </span>
              <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
                Đối chiếu Thị trường
              </h1>
              <p className="mt-3 text-base leading-relaxed text-slate-600 md:text-lg">
                So sánh mức spread, phí swap và thông số chi tiết của các sản phẩm Forex/CFD giữa nhiều nhà môi giới, quy đổi
                thành chi phí USD thực tế để ra quyết định dựa trên dữ liệu.
              </p>
            </div>
          </Reveal>

          {/* Scenario cards */}
          <Reveal delay={60}>
            <div className="mt-10">
              <ScenarioCards onSelect={goTo} />
            </div>
          </Reveal>

          {/* Studio nav */}
          <div className="mt-16">
            <Reveal>
              <div className="max-w-2xl">
                <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
                  Market Comparison Studio
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-500 md:text-base">
                  Một không gian so sánh dữ liệu nhẹ và chuyên nghiệp — chọn công cụ bên dưới để bắt đầu.
                </p>
              </div>
              <div className="mt-5">
                <StudioNav onSelect={goTo} />
              </div>
            </Reveal>
          </div>

          {/* Tools */}
          <div className="mt-12 space-y-16">
            <Reveal>
              <SpreadSection />
            </Reveal>
            <Reveal>
              <SwapSection />
            </Reveal>
            <Reveal>
              <ProductSection />
            </Reveal>
          </div>

          {/* Workflow + supporting modules */}
          <div className="mt-16 space-y-12">
            <Reveal>
              <WorkflowSection />
            </Reveal>
            <Reveal>
              <ToolChooser onSelect={goTo} />
            </Reveal>
            <Reveal>
              <MethodologySection />
            </Reveal>
            <Reveal>
              <NotesSection />
            </Reveal>
            <Reveal>
              <BrokerSection />
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
      <AppEnding />
    </div>
  );
}